/**
 * 블로그 상세 페이지.
 *
 * 영구지침: docs/develop/seo/blog-spec.md §6.2
 *
 * - react-markdown + remark-gfm으로 본문 렌더링
 * - ::cta directive를 인라인 CTA 카드로 변환
 * - 본문 하단 ctaPrimary/ctaSecondary 카드
 * - guide_bottom 광고 슬롯
 * - Article + BreadcrumbList JSON-LD
 * - 중복 글 301: 응답 slug ≠ URL slug 면 대표 글로 301 (ssr-spec §16.4.1)
 */
import { type ReactNode } from 'react'
import { Link, useParams, useLocation, Navigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Calendar, User, BookOpen, ArrowRight, ShieldCheck } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import { ArticleJsonLd, BreadcrumbJsonLd } from '@/components/common/JsonLd'
import PromotionSlot from '@/components/common/PromotionSlot'
import BlogRelatedLinks from '@/components/blog/BlogRelatedLinks'
import { useBlogPost, useBlogPosts, type BlogCta } from '@/features/blog/api'
import { BLOG_CATEGORY_LABELS } from '@/lib/blog/types'
import { ApiError } from '@/lib/api/types'
import { resolveBlogSlugRedirect, renderBlogSlugRedirect } from '@/lib/blog/blogSlugRedirect'
import { useSsrStatus } from '@/hooks/useSsrStatus'
import NotFoundPage from '@/pages/NotFoundPage'

const CTA_DIRECTIVE_REGEX = /^::cta\{([^}]+)\}$/

/**
 * `::cta{text="..." link="..." emoji="..."}` 형태의 한 줄을 BlogCta로 파싱.
 */
function parseCtaDirective(line: string): BlogCta | null {
  const match = line.trim().match(CTA_DIRECTIVE_REGEX)
  if (!match) return null
  const attrs = match[1]!
  const text = /text="([^"]+)"/.exec(attrs)?.[1]
  const link = /link="([^"]+)"/.exec(attrs)?.[1]
  const emoji = /emoji="([^"]+)"/.exec(attrs)?.[1]
  if (!text || !link) return null
  return { text, link, emoji }
}

/**
 * ReactNode를 plain text로 추출 (::cta directive 검출용).
 * ReactMarkdown이 emoji를 포함한 paragraph를 string + element 배열로 split해서
 * 전달하므로 단순 typeof 체크로는 검출이 안 된다.
 */
function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (typeof node === 'object' && 'props' in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children)
  }
  return ''
}

function CtaCard({ cta, variant = 'primary' }: { cta: BlogCta; variant?: 'primary' | 'secondary' }) {
  const isInternal = cta.link.startsWith('/')
  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    isInternal ? (
      <Link to={cta.link} className="block">{children}</Link>
    ) : (
      <a href={cta.link} target="_blank" rel="noopener noreferrer" className="block">
        {children}
      </a>
    )

  const bg =
    variant === 'primary'
      ? 'bg-gradient-to-br from-cb-primary to-cb-primary-hover text-white'
      : 'bg-cb-primary-light text-cb-primary border border-cb-primary/20'

  return (
    <Wrapper>
      <div className={`my-5 rounded-2xl p-5 flex items-center justify-between gap-3 ${bg} hover:shadow-lg transition-shadow`}>
        <div className="flex items-center gap-3">
          {cta.emoji && <span className="text-2xl">{cta.emoji}</span>}
          <span className="text-sm font-bold">{cta.text}</span>
        </div>
        <ArrowRight size={18} className="flex-shrink-0" />
      </div>
    </Wrapper>
  )
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: post, isLoading, isError, error } = useBlogPost(slug)
  // 관련 글: 같은 카테고리 다른 글 (최대 3개). 키는 SSR 프리페치 blogPostsQueryKey({ category, size: 4 }) 와 동일
  const { data: relatedPage } = useBlogPosts({
    category: post?.category,
    size: 4,
  })
  // SSR 응답 상태 전파 (서버 렌더 중에만 non-null) — 중복 글 301 용
  const ssrStatus = useSsrStatus()
  // 쿼리스트링 — 301 시 utm 등 유입 파라미터 보존 (SSR 은 StaticRouter 에 쿼리 포함 URL 이 들어온다)
  const { search } = useLocation()

  // ※ 아래 early return 들은 모든 훅 호출 이후에 위치해야 한다 (Rules of Hooks).

  // 글 미존재 확정 (soft-404 방지, ssr-spec §15.2) — 훅 호출 뒤에만 조기 return
  //  - SSR: 백엔드 404 면 프리페치가 ['blog-post', slug] 에 null 시드
  //  - CSR: axios(ApiError) status 404
  // 5xx/네트워크 실패는 404 로 오폭하지 않는다 (아래 일반 에러 처리).
  const isNotFound =
    post === null || (isError && error instanceof ApiError && error.status === 404)
  if (isNotFound) {
    return (
      <NotFoundPage
        title="글을 찾을 수 없습니다"
        description="요청하신 블로그 글이 없거나 더 이상 공개되지 않습니다."
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-32">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isError || !post) {
    return <Navigate to="/blog" replace />
  }

  // 중복 글 301 (ssr-spec §16.4.1) — 백엔드가 옛 slug 요청에 대표 글을 200 으로 주면 응답 slug 가 URL 과 다르다.
  // SSR: 301 + Location 후 null / CSR: <Navigate replace>. canonical·JSON-LD 는 아래에서 post.slug(대표 글) 기준.
  const redirectTarget = resolveBlogSlugRedirect(slug, post.slug, search)
  if (redirectTarget) {
    return renderBlogSlugRedirect(redirectTarget, ssrStatus)
  }

  const related = (relatedPage?.content ?? [])
    .filter((p) => p.slug !== post.slug)
    .slice(0, 3)
  const categoryLabel = BLOG_CATEGORY_LABELS[post.category]

  return (
    <>
      {/* 브랜드는 SEO 컴포넌트가 " | 케어밸런스" 로 1회만 붙인다 (blog-cms-spec §7.3) */}
      <SEO
        title={post.title}
        description={post.summary}
        url={`/blog/${post.slug}`}
        image={post.thumbnailUrl ?? undefined}
        type="article"
      />
      <ArticleJsonLd
        headline={post.title}
        description={post.summary}
        image={post.thumbnailUrl ?? undefined}
        datePublished={post.publishedAt?.slice(0, 10) ?? ''}
        dateModified={post.updatedAt?.slice(0, 10) ?? undefined}
        author={post.author}
        slug={post.slug}
      />
      <BreadcrumbJsonLd
        items={[
          { name: '홈', url: '/' },
          { name: '블로그', url: '/blog' },
          { name: categoryLabel, url: `/blog/category/${post.category}` },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
      />
      <SubpageHero
        title={post.title}
        description={post.summary}
        icon={<BookOpen size={20} className="text-white" />}
      />
      <PageContainer noMaxWidth>
        <article className="max-w-3xl mx-auto py-6 px-4">
          {/* 메타정보 */}
          <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <Link
              to={`/blog/category/${post.category}`}
              className="rounded-full bg-cb-primary-light px-3 py-1 text-xs font-semibold text-cb-primary hover:bg-cb-primary/20"
            >
              {categoryLabel}
            </Link>
            <span className="flex items-center gap-1">
              <User size={14} />
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {post.publishedAt?.slice(0, 10) ?? ''}
              {post.updatedAt && post.updatedAt.slice(0, 10) !== post.publishedAt?.slice(0, 10) && (
                <span className="ml-1 text-gray-400">(수정 {post.updatedAt.slice(0, 10)})</span>
              )}
            </span>
          </div>

          {/* 본문 (마크다운 + ::cta directive)
              @tailwindcss/typography 미사용 → ReactMarkdown components prop으로
              모든 element에 직접 className 부여 (의존성 0). */}
          <div className="text-gray-800 leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="mt-10 mb-4 text-3xl font-extrabold text-gray-900 border-b border-gray-200 pb-2">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="mt-10 mb-4 text-2xl font-bold text-gray-900 border-l-4 border-cb-primary pl-3">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="mt-7 mb-3 text-xl font-bold text-gray-900">
                    {children}
                  </h3>
                ),
                h4: ({ children }) => (
                  <h4 className="mt-6 mb-2 text-lg font-bold text-gray-900">
                    {children}
                  </h4>
                ),
                p: ({ children, ...props }) => {
                  // ::cta directive 처리 (children을 string으로 추출하여 검사)
                  const text = extractText(children)
                  const cta = parseCtaDirective(text)
                  if (cta) return <CtaCard cta={cta} variant="secondary" />
                  return (
                    <p className="my-4 text-base text-gray-700 leading-7" {...props}>
                      {children}
                    </p>
                  )
                },
                ul: ({ children }) => (
                  <ul className="my-4 ml-5 list-disc space-y-1.5 text-gray-700">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="my-4 ml-5 list-decimal space-y-1.5 text-gray-700">
                    {children}
                  </ol>
                ),
                li: ({ children }) => <li className="leading-7">{children}</li>,
                strong: ({ children }) => (
                  <strong className="font-bold text-gray-900">{children}</strong>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                blockquote: ({ children }) => (
                  <blockquote className="my-5 border-l-4 border-cb-primary/40 bg-cb-primary-light/30 px-4 py-3 text-sm text-gray-700 rounded-r-lg">
                    {children}
                  </blockquote>
                ),
                hr: () => <hr className="my-8 border-gray-200" />,
                code: ({ children, ...props }) => {
                  // inline code
                  if (!('className' in props) || !(props as { className?: string }).className) {
                    return (
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm text-cb-primary font-mono">
                        {children}
                      </code>
                    )
                  }
                  // code block
                  return (
                    <code className="block bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
                      {children}
                    </code>
                  )
                },
                pre: ({ children }) => (
                  <pre className="my-5 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm">
                    {children}
                  </pre>
                ),
                table: ({ children }) => (
                  <div className="my-5 overflow-x-auto">
                    <table className="w-full border-collapse text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => (
                  <tr className="border-b border-gray-200">{children}</tr>
                ),
                th: ({ children }) => (
                  <th className="border border-gray-200 px-4 py-2 text-left font-bold text-gray-900">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-gray-200 px-4 py-2 text-gray-700">
                    {children}
                  </td>
                ),
                a: ({ href, children, ...props }) => {
                  if (href?.startsWith('/')) {
                    return (
                      <Link to={href} className="text-cb-primary font-semibold hover:underline">
                        {children}
                      </Link>
                    )
                  }
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cb-primary font-semibold hover:underline"
                      {...props}
                    >
                      {children}
                    </a>
                  )
                },
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* 본문 하단 CTA */}
          {post.ctaPrimary && <CtaCard cta={post.ctaPrimary} variant="primary" />}
          {post.ctaSecondary && <CtaCard cta={post.ctaSecondary} variant="secondary" />}

          {/* E-E-A-T 신뢰 블록 — 작성 주체·데이터 출처·최종 갱신일·의료조언 아님 면책(YMYL) */}
          <AuthorDisclosure
            author={post.author}
            publishedAt={post.publishedAt}
            updatedAt={post.updatedAt}
          />

          {/* 함께 보면 좋은 정보 — 도구·가이드 + 글에 언급된 지역 랜딩 (blog-internal-links-spec.md) */}
          <BlogRelatedLinks post={post} />

          {/* 광고 슬롯: guide_bottom (광고 시스템 1단계 재사용) */}
          <PromotionSlot slotKey="guide_bottom" className="mt-6" />

          {/* 관련 글 */}
          {related.length > 0 && (
            <section className="mt-12 border-t border-gray-200 pt-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">관련 글</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group rounded-xl border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-cb-primary">
                      {r.title}
                    </h3>
                    <p className="mt-1 text-xs text-gray-500 line-clamp-2">{r.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </PageContainer>
    </>
  )
}

/**
 * 블로그 하단 E-E-A-T 신뢰 블록 — 작성 주체·데이터 출처·최종 갱신일·의료조언 아님 면책.
 * YMYL(요양·의료·재정) 니치의 신뢰성 신호를 명시적으로 노출해 콘텐츠 가치를 보강한다.
 */
function AuthorDisclosure({
  author,
  publishedAt,
  updatedAt,
}: {
  author: string
  publishedAt: string | null
  updatedAt: string | null
}) {
  const updated = (updatedAt ?? publishedAt)?.slice(0, 10) ?? ''
  return (
    <footer className="mt-10 rounded-2xl border border-gray-200 bg-gray-50 p-5">
      <div className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
        <ShieldCheck size={16} className="text-cb-primary" />
        이 글의 신뢰 정보
      </div>
      <dl className="mt-3 space-y-1.5 text-sm text-gray-600">
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-gray-400">작성·감수</dt>
          <dd>{author}</dd>
        </div>
        {updated && (
          <div className="flex gap-2">
            <dt className="w-20 shrink-0 text-gray-400">최종 갱신</dt>
            <dd>{updated}</dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="w-20 shrink-0 text-gray-400">데이터 출처</dt>
          <dd>국민건강보험공단 장기요양보험 공개 데이터, 보건복지부 고시 등 공공 출처</dd>
        </div>
      </dl>
      <p className="mt-3 border-t border-gray-200 pt-3 text-xs leading-5 text-gray-500">
        본 콘텐츠는 요양·돌봄에 관한 일반적 정보 제공을 목적으로 하며, 의료·법률·재정 자문을 대체하지
        않습니다. 장기요양등급 판정·본인부담금·입소 등 개별 사안은 국민건강보험공단(☎ 1577-1000)과 관할
        기관, 담당 전문가에게 확인하시기 바랍니다.
      </p>
    </footer>
  )
}
